import { useState } from "react";
import { CookingPot, Soup, UtensilsCrossed, SprayCan, Armchair, Zap, Shirt, Package, Wheat, Boxes, MoreHorizontal, Eye, Pencil,
  FileStack, LayoutGrid, ShieldCheck, Wrench, ArrowLeftRight, Landmark, Receipt, CalendarClock, DoorOpen, Scale, FileText } from "lucide-react";
import { metalFill } from "@/components/sales/SalesQuickFilter";

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const CAT_ICONS = [[/EQUIP/i, CookingPot], [/UTENS/i, Soup], [/CUTLER|CROCK/i, UtensilsCrossed], [/HOUSE|CLEAN/i, SprayCan], [/FURN|FIXT/i, Armchair],
  [/ELECT/i, Zap], [/UNIFORM/i, Shirt], [/PACK/i, Package], [/RAW/i, Wheat]];
export const invCatIcon = (code) => (CAT_ICONS.find(([re]) => re.test(code || "")) || [null, MoreHorizontal])[1];

export function InvCategoryBlocks({ items, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid={`${testid}-blocks`}>
      {items.map((c, i) => {
        const Icon = c.code ? invCatIcon(c.code) : Boxes;
        const active = (value || "") === c.code;
        return (
          <button key={c.code || "all"} type="button" onClick={() => onChange(c.code)} aria-pressed={active}
            className={`bi-tile !min-h-[104px] !flex-row !items-center gap-3 ${active ? "ring-4 ring-[#F4D58D] scale-[1.02]" : ""}`}
            style={{ "--bi-fill": metalFill(i), animationDelay: `${Math.min(i, 12) * 25}ms` }} data-testid={`${testid}-${(c.code || "all").toLowerCase()}`}>
            <Icon className="h-9 w-9 shrink-0 text-[#F4D58D]" />
            <span className="flex min-w-0 flex-col text-left">
              <span className="break-words text-base font-bold leading-tight">{c.name}</span>
              <span className="text-sm opacity-90" data-testid={`${testid}-${(c.code || "all").toLowerCase()}-count`}>{c.count} item{c.count === 1 ? "" : "s"}</span>
              {c.sub && <span className="text-xs opacity-90">{c.sub}</span>}
            </span>
          </button>);
      })}
    </div>
  );
}

const KV = ({ l, v }) => <div className="flex justify-between gap-2 text-sm"><span className="text-[#5a3a22]">{l}</span><b className="text-right break-words">{v || "—"}</b></div>;

export function AssetCards({ rows, catName, onView, onEdit }) {
  const [limit, setLimit] = useState(24);
  if (!rows.length) return <p className="bi-panel text-base text-muted-foreground" data-testid="inv-cards-empty">No assets in this selection.</p>;
  return (
    <div className="space-y-3" data-testid="inv-asset-cards">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.slice(0, limit).map((r) => {
          const Icon = invCatIcon(r.category_code);
          return (
            <div key={r.asset_id} className="rounded-2xl border-2 border-[#ead6a6] bg-white p-4 shadow-sm" data-testid={`inv-card-${r.asset_id}`}>
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[#F4D58D]" style={{ background: metalFill(0) }}><Icon className="h-6 w-6" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-lg font-bold leading-tight text-[#750817]">{r.asset_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{r.asset_code}</span>
                </span>
                <span className="text-right"><span className="block text-xs text-muted-foreground">Value</span><b className="font-mono text-base">{money(r.current_asset_value)}</b></span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-[#fbf0d6] px-3 py-1">{catName(r.category_code)}{r.subcategory ? ` / ${r.subcategory}` : ""}</span>
                <span className="rounded-full bg-[#fbf0d6] px-3 py-1">Qty {r.quantity_current} {r.unit}</span>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">{r.condition || "—"}</span>
                <span className="rounded-full bg-[#f3e8ff] px-3 py-1 text-[#4c1d95]">{r.status || "—"}</span>
              </div>
              <div className="mt-3 space-y-1">
                <KV l="Rate" v={money(r.purchase_rate)} /><KV l="Vendor" v={r.vendor} /><KV l="Location" v={r.location} />
                <KV l="Responsible" v={r.responsible_person} /><KV l="Last verified" v={r.last_verified_date ? String(r.last_verified_date).slice(0, 10) : ""} />
              </div>
              <div className="mt-3 flex gap-2">
                <button type="button" className="bi-pill" onClick={() => onView(r)} data-testid={`inv-card-view-${r.asset_id}`}><Eye className="h-4 w-4" />View</button>
                {!r.is_raw_material_link && <button type="button" className="bi-pill" onClick={() => onEdit(r)} data-testid={`inv-card-edit-${r.asset_id}`}><Pencil className="h-4 w-4" />Edit</button>}
              </div>
            </div>);
        })}
      </div>
      {rows.length > limit && <button type="button" className="bi-pill" onClick={() => setLimit(limit + 24)} data-testid="inv-cards-more">Show more ({rows.length - limit} left)</button>}
    </div>
  );
}

const REP_ICONS = { asset_register: FileStack, category_wise: LayoutGrid, condition_wise: ShieldCheck, not_verified: CalendarClock, under_repair: Wrench,
  transferred: ArrowLeftRight, value_by_center: Landmark, purchase_history: Receipt, warranty_expiring: CalendarClock, exit_handover: DoorOpen, verification_variance: Scale };

export function ReportBlocks({ reports, value, onPick }) {
  return (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="inv-report-blocks">
      {reports.map((r, i) => {
        const Icon = REP_ICONS[r.id] || FileText;
        const active = value === r.id;
        return (
          <button key={r.id} type="button" onClick={() => onPick(r.id)} aria-pressed={active}
            className={`bi-tile !min-h-[112px] !items-center !justify-center gap-2 text-center ${active ? "ring-4 ring-[#F4D58D] scale-[1.02]" : ""}`}
            style={{ "--bi-fill": metalFill(i), animationDelay: `${Math.min(i, 12) * 25}ms` }} data-testid={`inv-report-block-${r.id}`}>
            <Icon className="h-9 w-9 text-[#F4D58D]" />
            <span className="text-base font-bold leading-tight">{r.label}</span>
          </button>);
      })}
    </div>
  );
}
